import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import type { Customer } from '../types/customer';
import { EMAIL_REGEX, MOBILE_REGEX, AADHAR_REGEX, UDHYOG_AADHAR_REGEX, GST_REGEX, PAN_REGEX } from '../utils/regex';



interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FormData) => Promise<void>;
  editData?: Customer | null;
}

const CustomerFormModal: React.FC<Props> = ({ isOpen, onClose, onSave, editData }) => {
  const [formData, setFormData] = useState({
    firmName: '',
    name: '',
    email: '',
    permanentAddress: '',
    mobileNumber1: '',
    mobileNumberRef1:'',
    mobileNumber2: '',
    mobileNumberRef2:'',
    mobileNumber3: '',
    mobileNumberRef3:'',
    mobileNumber4: '',
    mobileNumberRef4:'',
    mobileNumber5: '',
    mobileNumberRef5:'',
    gstNumber: '',
    udhyogAadharNumber: '',
    udhyogAadharCategory: '',
    city: '',
    state: '',
    aadharCardNumber: '',
    panCardNumber:''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        firmName: editData.firmName || '',
        email: editData.email || '',
        permanentAddress: editData.permanentAddress || '',
        mobileNumber1: editData.mobileNumber1 || '',
        mobileNumberRef1: editData.mobileNumberRef1 || '',
        mobileNumber2: editData.mobileNumber2 || '',
        mobileNumberRef2: editData.mobileNumberRef2 || '',
        mobileNumber3: editData.mobileNumber3 || '',
        mobileNumberRef3: editData.mobileNumberRef3 || '',
        mobileNumber4: editData.mobileNumber4 || '',
        mobileNumberRef4: editData.mobileNumberRef4 || '',
        mobileNumber5: editData.mobileNumber5 || '',
        mobileNumberRef5: editData.mobileNumberRef5 || '',
        udhyogAadharCategory: editData.udhyogAadharCategory || '',
        udhyogAadharNumber: editData.udhyogAadharNumber || '',
        city: editData.city || '',
        state: editData.state || '',
        aadharCardNumber: editData.aadharCardNumber || '',
        gstNumber: editData.gstNumber || '',
        panCardNumber: editData.panCardNumber || ''
      });
    }
  }, [editData]);

  // const { getRootProps, getInputProps } = useDropzone({
  //   accept: { 'image/*': [] },
  //   maxFiles: 1,
  //   onDrop: (acceptedFiles: FileWithPath[]) => {
  //     if (acceptedFiles?.[0]) {
  //       setAadharPhoto(acceptedFiles[0]);
  //     }
  //   }
  // });


  // Controlled file states for image uploads (shared component used for UI)
  const [panCardFile, setPanCardFile] = useState<File | null>(null);
  const [gstFile, setGstFile] = useState<File | null>(null);
  const [udhyogAadharFile, setUdhyogAadharFile] = useState<File | null>(null);


  // Import and use the shared ImageUpload component for file uploads
  // (See ImageUpload.tsx)

  // Update handleClear to also reset errors
  const handleClear = () => {
    setFormData({
      name: '', firmName: '', email: '',
      permanentAddress: '', mobileNumber1: '', mobileNumberRef1: '', mobileNumber2: '', mobileNumberRef2: '',
      mobileNumber3: '', mobileNumberRef3: '', mobileNumber4: '', mobileNumberRef4: '', mobileNumber5: '', mobileNumberRef5: '',
      gstNumber: '', udhyogAadharNumber: '', udhyogAadharCategory: '', city: '', state: '',
      aadharCardNumber: '', panCardNumber: ''
    });
    setPanCardFile(null);
    setGstFile(null);
    setUdhyogAadharFile(null);
    setErrors({});
  };

  // Update onClose to clear form
  const handleClose = () => {
    handleClear();
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields: Name, Firm Name, Email, Mobile No.1, Address
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.firmName.trim()) newErrors.firmName = "Firm name is required";

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., example@domain.com)';
    }

    // Mobile number 1 required
    if (!formData.mobileNumber1) {
      newErrors.mobileNumber1 = 'Mobile number 1 is required';
    } else if (!MOBILE_REGEX.test(formData.mobileNumber1)) {
      newErrors.mobileNumber1 = 'Please enter a valid 10-digit mobile number';
    }

    // Other mobile numbers are optional but validated if present
    if (formData.mobileNumber2 && !MOBILE_REGEX.test(formData.mobileNumber2)) {
      newErrors.mobileNumber2 = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.mobileNumber3 && !MOBILE_REGEX.test(formData.mobileNumber3)) {
      newErrors.mobileNumber3 = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.mobileNumber4 && !MOBILE_REGEX.test(formData.mobileNumber4)) {
      newErrors.mobileNumber4 = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.mobileNumber5 && !MOBILE_REGEX.test(formData.mobileNumber5)) {
      newErrors.mobileNumber5 = 'Please enter a valid 10-digit mobile number';
    }

    // Address validation
    if (!formData.permanentAddress.trim()) {
      newErrors.permanentAddress = 'Address is required';
    }

    // Optional fields: validate only if provided
    if (formData.gstNumber && !GST_REGEX.test(formData.gstNumber)) {
      newErrors.gstNumber = 'Please enter a valid GST number';
    }

    if (formData.udhyogAadharNumber && !UDHYOG_AADHAR_REGEX.test(formData.udhyogAadharNumber)) {
      newErrors.udhyogAadharNumber = 'Please enter a valid Udhyog Aadhar number';
    }

    if (formData.aadharCardNumber && !AADHAR_REGEX.test(formData.aadharCardNumber)) {
      newErrors.aadharCardNumber = 'Please enter a valid 12-digit Aadhar number';
    }

    if (formData.panCardNumber && !PAN_REGEX.test(formData.panCardNumber)) {
      newErrors.panCardNumber = 'Please enter a valid pan card number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const data = new FormData();

      // Append all form fields, including empty ones
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value ?? '');
      });

      if (panCardFile) data.append("panCardPhoto", panCardFile);
      if (gstFile) data.append("gstPhoto", gstFile);
      if (udhyogAadharFile) data.append("udhyogAadharPhoto", udhyogAadharFile);

      await onSave(data);
      handleClose(); // Use handleClose instead of onClose to clear form
    } catch (err) {
      console.error('Failed to save customer:', err);
      alert('Failed to save customer. Please try again.'); // Add user feedback
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getErrorDisplay = (fieldName: string) => {
    return errors[fieldName] ? (
      <p className="text-red-500 text-xs mt-1">{errors[fieldName]}</p>
    ) : null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {editData ? 'Update Customer Details' : 'Add New Customer'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            title="Close"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Main Content Area */}
            <div className="flex gap-6">
              {/* Left Side - Main Form Fields */}
              <div className="flex-1">
                {/* First Row - Full Width */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Firm Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      name="firmName"
                      value={formData.firmName}
                      onChange={handleChange}
                      placeholder="Enter firm name"
                      className="input-field"
                    />
                    {getErrorDisplay('firmName')}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter owner name"
                      className="input-field"
                    />
                    {getErrorDisplay('name')}
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Surname <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleChange}
                      className="input-field"
                    />
                    {getErrorDisplay('surname')}
                  </div> */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="example@domain.com"
                      className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {getErrorDisplay('email')}
                  </div>
                </div>

                {/* Mobile -1,2,3,4 and address */}
                <div className="flex gap-6 mb-6">
                  <div className="flex-1 space-y-6">
                    {/*Mobile 1 and Mobile 2*/}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile No. 1 <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                              <input
                            type="text"
                            name="mobileNumberRef1"
                            value={formData.mobileNumberRef1}
                            onChange={handleChange}
                            placeholder="Ref (e.g., Self / Office)"
                            className={`input-field ${errors.mobileNumberRef1 ? 'border-red-500' : ''}`}
                          />

                          <input
                            required
                            type="tel"
                            name="mobileNumber1"
                            value={formData.mobileNumber1}
                            onChange={handleChange}
                            maxLength={10}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="Enter 10-digit mobile number"
                            className={`input-field ${errors.mobileNumber1 ? 'border-red-500' : ''}`}
                          />
                        </div>
                        {getErrorDisplay('mobileNumber1')}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile No. 2
                        </label>
                         <div className="flex gap-2">
                          <input
                          type="text"
                          name="mobileNumberRef2"
                          value={formData.mobileNumberRef2}
                          onChange={handleChange}
                          placeholder="Ref (e.g., Spouse / Office)"
                          className={`input-field ${errors.mobileNumberRef2 ? 'border-red-500' : ''}`}
                        />
                        <input
                          type="tel"
                          name="mobileNumber2"
                          value={formData.mobileNumber2}
                          onChange={handleChange}
                          maxLength={10}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="Enter 10-digit mobile number"
                          className={`input-field ${errors.mobileNumber2 ? 'border-red-500' : ''}`}
                        />
                        </div>
                        {getErrorDisplay('mobileNumber2')}
                      </div>
                    </div>

                    {/* Mobile 3 and 4 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile No. 3
                        </label>
                         <div className="flex gap-2">
                        <input
                          type="text"
                          name="mobileNumberRef3"
                          value={formData.mobileNumberRef3}
                          onChange={handleChange}
                          placeholder="Ref (optional)"
                          className={`input-field ${errors.mobileNumberRef3 ? 'border-red-500' : ''}`}
                        />
                         <input
                          type="tel"
                          name="mobileNumber3"
                          value={formData.mobileNumber3}
                          onChange={handleChange}
                          placeholder="Enter 10-digit mobile number (optional)"
                          className={`input-field ${errors.mobileNumber3 ? 'border-red-500' : ''}`}
                        />
                        </div>
                        {getErrorDisplay('mobileNumber3')}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile No. 4
                        </label>
                         <div className="flex gap-2">
                        <input
                          type="text"
                          name="mobileNumberRef4"
                          value={formData.mobileNumberRef4}
                          onChange={handleChange}
                          placeholder="Ref (optional)"
                          className={`input-field ${errors.mobileNumberRef4 ? 'border-red-500' : ''}`}
                        />

                         <input
                          type="tel"
                          name="mobileNumber4"
                          value={formData.mobileNumber4}
                          onChange={handleChange}
                          placeholder="Enter 10-digit mobile number (optional)"
                          className={`input-field ${errors.mobileNumber4 ? 'border-red-500' : ''}`}
                        />
                        </div>
                        {getErrorDisplay('mobileNumber4')}
                      </div>
                    </div>

                    {/* Mobile 5 and category */}
                    {/* <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile No. 5
                        </label>
                         <div className="flex gap-2">
                        <input
                          type="tel"
                          name="mobileNumber5"
                          value={formData.mobileNumber5}
                          onChange={handleChange}
                          className={`input-field ${errors.mobileNumber5 ? 'border-red-500' : ''}`}
                        />

                        <input
                          type="tel"
                          name="mobileNumber5"
                          value={formData.mobileNumber5}
                          onChange={handleChange}
                          className={`input-field ${errors.mobileNumber5 ? 'border-red-500' : ''}`}
                        />
                        </div>
                        {getErrorDisplay('mobileNumber5')}
                      </div>
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Udhyog Aadhar Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        name="udhyogAadharCategory"
                        value={formData.udhyogAadharCategory}
                        onChange={handleChange}
                        className="w-full border rounded-md p-2"
                      >
                        <option value="">Select Category</option>
                        <option value="Micro">Micro</option>
                        <option value="Small">Small</option>
                        <option value="Medium">Medium</option>
                        <option value="Large">Large</option>
                      </select>
                      {getErrorDisplay('udhyogAadharCategory')}
                    </div>
                    </div> */}
                  </div>

                  {/* Address - Right Side */}
                  <div className="w-80">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      name="permanentAddress"
                      value={formData.permanentAddress}
                      onChange={handleChange}
                      rows={5}
                      className="input-field resize-none"
                      placeholder="Enter complete address..."
                    />
                    {getErrorDisplay('permanentAddress')}
                  </div>
                </div>

                {/* Remaining Full Width Rows */}
                <div className="space-y-6">

                  {/* GST, Udhyog Details */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mobile No. 5
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="mobileNumberRef5"
                          value={formData.mobileNumberRef5}
                          onChange={handleChange}
                          placeholder="Ref (optional)"
                          className={`input-field ${errors.mobileNumberRef5 ? 'border-red-500' : ''}`}
                        />
                        <input
                          type="tel"
                          name="mobileNumber5"
                          value={formData.mobileNumber5}
                          onChange={handleChange}
                          placeholder="Enter 10-digit mobile number (optional)"
                          className={`input-field ${errors.mobileNumber5 ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {getErrorDisplay('mobileNumber5')}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Enter city"
                        className="input-field"
                      />
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="Enter state"
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                     <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adhar Card No.
                      </label>
                      <input
                        type="text"
                        name="aadharCardNumber"
                        value={formData.aadharCardNumber}
                        onChange={handleChange}
                        maxLength={12}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter 12-digit Aadhar number"
                        className={`input-field ${errors.aadharCardNumber ? 'border-red-500' : ''}`}
                      />
                      {getErrorDisplay('aadharCardNumber')}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Udhyog Aadhar Category
                      </label>
                      <select
                        name="udhyogAadharCategory"
                        value={formData.udhyogAadharCategory}
                        onChange={handleChange}
                        className="input-field"
                      >
                        <option value="">Select Category</option>
                        <option value="Micro">Micro</option>
                        <option value="Small">Small</option>
                        <option value="Medium">Medium</option>
                        <option value="Large">Large</option>
                      </select>
                      {getErrorDisplay('udhyogAadharCategory')}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Udhyog Aadhar No.
                      </label>
                      <input
                        type="text"
                        name="udhyogAadharNumber"
                        value={formData.udhyogAadharNumber}
                        onChange={handleChange}
                        placeholder="Enter Udhyog Aadhar number"
                        className={`input-field ${errors.udhyogAadharNumber ? 'border-red-500' : ''}`}
                      />
                      {getErrorDisplay('udhyogAadharNumber')}
                    </div>
                   
                  </div>

                  {/* Pan, GST, Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PAN Card No.
                      </label>
                      <input
                        type="text"
                        name="panCardNumber"
                        value={formData.panCardNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, panCardNumber: e.target.value.toUpperCase() }))}
                        maxLength={10}
                        placeholder="Eg: ABCDE1234F"
                        className={`input-field ${errors.panCardNumber ? 'border-red-500' : ''}`}
                      />
                      {getErrorDisplay('panCardNumber')}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GST No.
                      </label>
                      <input
                        type="text"
                        name="gstNumber"
                        value={formData.gstNumber}
                        onChange={handleChange}
                        placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
                        className={`input-field ${errors.gstNumber ? 'border-red-500' : ''}`}
                      />
                      {getErrorDisplay('gstNumber')}
                    </div>
                  </div>

                  {/* Pan, GST,  Details */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <ImageUpload
                        label="PAN Card Photo"
                        existingPath={editData?.panCardPhoto}
                        file={panCardFile}
                        onFileChange={setPanCardFile}
                        maxPreviewHeight="128px"
                        maxPreviewWidth="100%"
                      />
                      {errors.panCardPhoto && <p className="text-red-500 text-xs mt-1">{errors.panCardPhoto}</p>}
                    </div>
                    <div>
                      <ImageUpload
                        label="GST Photo"
                        existingPath={editData?.gstPhoto}
                        file={gstFile}
                        onFileChange={setGstFile}
                        maxPreviewHeight="128px"
                        maxPreviewWidth="100%"
                      />
                      {errors.gstPhoto && <p className="text-red-500 text-xs mt-1">{errors.gstPhoto}</p>}
                    </div>
                    <div>
                      <ImageUpload
                        label="Udhyog adhar Photo"
                        existingPath={editData?.udhyogAadharPhoto}
                        file={udhyogAadharFile}
                        onFileChange={setUdhyogAadharFile}
                        maxPreviewHeight="128px"
                        maxPreviewWidth="100%"
                      />
                      {errors.udhyogAadharPhoto && <p className="text-red-500 text-xs mt-1">{errors.udhyogAadharPhoto}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Buttons */}
            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={handleClear}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
              >
                {loading ? 'Saving...' : editData ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerFormModal;
